import React, { useState, useEffect } from "react";
import { useCart } from "../contexts/CartContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getCurrentUserProfile } from "../services/api"; // Ajout pour vérifier les informations utilisateur

const Cart = () => {
  const {
    items,
    totalItems,
    totalPrice,
    updateQuantity,
    confirmRemoveItem,
    confirmClearCart,
    toggleSubscription,
  } = useCart();

  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Restaurer le code promo du localStorage si disponible
  useEffect(() => {
    const savedDiscount = localStorage.getItem("cartDiscount");
    const savedCouponCode = localStorage.getItem("couponCode");

    if (savedDiscount) {
      setDiscount(parseFloat(savedDiscount));
    }

    if (savedCouponCode) {
      setCouponCode(savedCouponCode);
    }
  }, []);

  // Calculer les économies réalisées grâce aux abonnements
  const calculateSubscriptionSavings = () => {
    let savings = 0;
    items.forEach((item) => {
      if (item.isSubscription) {
        // 15% d'économie sur les articles en abonnement
        savings += item.price * item.quantity * 0.15;
      }
    });
    return savings;
  };

  const subscriptionSavings = calculateSubscriptionSavings();
  const hasSubscriptionItems = items.some((item) => item.isSubscription);

  // Gestion des codes promo (simulation)
  const applyCoupon = () => {
    if (!couponCode.trim()) {
      toast.warning("Veuillez entrer un code promo");
      return;
    }

    if (couponCode.toLowerCase() === "promo10") {
      setDiscount(0.1);
      localStorage.setItem("cartDiscount", "0.1");
      localStorage.setItem("couponCode", couponCode);
      toast.success("Code promo appliqué : -10%");
    } else if (couponCode.toLowerCase() === "promo20") {
      setDiscount(0.2);
      localStorage.setItem("cartDiscount", "0.2");
      localStorage.setItem("couponCode", couponCode);
      toast.success("Code promo appliqué : -20%");
    } else if (couponCode.toLowerCase() === "bienvenue") {
      setDiscount(0.1);
      localStorage.setItem("cartDiscount", "0.1");
      localStorage.setItem("couponCode", couponCode);
      toast.success("Code promo BIENVENUE appliqué : -10%");
    } else {
      toast.error("Code promo invalide");
    }
  };

  // Calcul du montant après remise
  const discountAmount = totalPrice * discount;
  const finalTotal = totalPrice - discountAmount;

  // Fonction pour vérifier si le profil utilisateur est complet
  const checkUserProfile = async () => {
    try {
      setIsProcessing(true);

      // Vérifier si l'utilisateur est connecté
      const token = localStorage.getItem("token");
      if (!token) {
        toast.warning("Veuillez vous connecter pour finaliser votre commande");
        navigate("/login", { state: { from: "/cart" } });
        return false;
      }

      // Récupérer le profil utilisateur
      const response = await getCurrentUserProfile();
      const user = response.data;

      // Vérifier les informations essentielles
      const missingInfo = [];

      if (!user.firstName || !user.lastName) {
        missingInfo.push("nom complet");
      }

      if (!user.email) {
        missingInfo.push("email");
      }

      if (!user.phone) {
        missingInfo.push("numéro de téléphone");
      }

      // Vérifier l'adresse
      const address = user.address || {};
      if (
        !address.street ||
        !address.postalCode ||
        !address.city ||
        !address.country
      ) {
        missingInfo.push("adresse complète");
      }

      if (missingInfo.length > 0) {
        // Informer l'utilisateur des informations manquantes
        toast.info(
          `Veuillez compléter les informations suivantes avant de procéder au paiement : ${missingInfo.join(
            ", "
          )}.`
        );

        // Rediriger vers la page de profil avec un indicateur
        navigate("/profile?checkout=pending");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Erreur lors de la vérification du profil:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // Gestion du checkout - modifiée pour vérifier le profil
  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error("Votre panier est vide");
      return;
    }

    // Vérifier si le profil utilisateur est complet
    const isProfileComplete = await checkUserProfile();

    if (isProfileComplete) {
      // Si le profil est complet, rediriger vers la page checkout
      navigate("/checkout");
    }
  };

  return (
    <div className="container py-5">
      <h1 className="mb-4">Mon Panier</h1>

      {items.length === 0 ? (
        <div className="text-center py-5">
          <div className="mb-4">
            <i className="bi bi-cart3 display-1 text-muted"></i>
          </div>
          <h3 className="mt-3">Votre panier est vide</h3>
          <p className="text-muted mb-4">
            Découvrez nos box et ajoutez-les à votre panier
          </p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate("/category")}
          >
            <i className="bi bi-box me-2"></i>
            Découvrir nos box
          </button>
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-lg-8">
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-white py-3">
                  <h5 className="card-title mb-0">Articles ({totalItems})</h5>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {items.map((item) => (
                      <div
                        key={`${item.id}-${
                          item.isSubscription ? "sub" : "single"
                        }`}
                        className="list-group-item"
                      >
                        <div className="d-flex w-100 flex-wrap">
                          <div className="d-flex flex-grow-1 mb-3 mb-md-0">
                            <div className="flex-shrink-0">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="rounded"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <div className="d-flex justify-content-between">
                                <h5 className="mb-1">
                                  {item.name}
                                  {item.isSubscription && (
                                    <span
                                      className="ms-2 badge"
                                      style={{
                                        backgroundColor: "#f89638",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      -15%
                                    </span>
                                  )}
                                </h5>
                                <span className="text-primary fw-bold d-md-none">
                                  {(
                                    item.price *
                                    (item.isSubscription ? 0.85 : 1) *
                                    item.quantity
                                  ).toFixed(2)}{" "}
                                  €
                                </span>
                              </div>
                              <p className="text-muted small mb-1">
                                {item.category}
                              </p>
                              <div className="d-flex align-items-center">
                                <p className="text-primary fw-bold mb-0 d-none d-md-block me-3">
                                  {(
                                    item.price *
                                    (item.isSubscription ? 0.85 : 1)
                                  ).toFixed(2)}{" "}
                                  €
                                  {item.isSubscription && (
                                    <span className="text-decoration-line-through text-muted ms-2 small">
                                      {item.price.toFixed(2)} €
                                    </span>
                                  )}
                                </p>
                                {item.hasSubscriptionOption && (
                                  <button
                                    className="btn btn-sm btn-link text-decoration-none p-0"
                                    onClick={() =>
                                      toggleSubscription(
                                        item.id,
                                        item.name,
                                        item.isSubscription
                                      )
                                    }
                                  >
                                    <small>
                                      {item.isSubscription
                                        ? "Passer en achat unique"
                                        : "Option abonnement (-15%)"}
                                    </small>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex align-items-start justify-content-between w-100 w-md-auto">
                            <div
                              className="input-group input-group-sm me-2"
                              style={{ maxWidth: "120px" }}
                            >
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() =>
                                  updateQuantity(
                                    item.id,
                                    item.quantity - 1,
                                    item.isSubscription
                                  )
                                }
                                disabled={isProcessing}
                              >
                                <i className="bi bi-dash"></i>
                              </button>
                              <input
                                type="text"
                                className="form-control text-center"
                                value={item.quantity}
                                readOnly
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() =>
                                  updateQuantity(
                                    item.id,
                                    item.quantity + 1,
                                    item.isSubscription
                                  )
                                }
                                disabled={isProcessing}
                              >
                                <i className="bi bi-plus"></i>
                              </button>
                            </div>
                            <div className="d-flex flex-column align-items-end">
                              <span className="text-primary fw-bold d-none d-md-block mb-2">
                                {(
                                  item.price *
                                  (item.isSubscription ? 0.85 : 1) *
                                  item.quantity
                                ).toFixed(2)}{" "}
                                €
                              </span>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  confirmRemoveItem(
                                    item.id,
                                    item.name,
                                    item.isSubscription
                                  )
                                }
                                disabled={isProcessing}
                              >
                                <i className="bi bi-trash me-1"></i>
                                <span className="d-none d-md-inline">
                                  Supprimer
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card-footer bg-white py-3">
                  <div className="d-flex justify-content-between">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => navigate("/category")}
                      disabled={isProcessing}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Continuer mes achats
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      onClick={confirmClearCart}
                      disabled={isProcessing}
                    >
                      <i className="bi bi-trash me-2"></i>
                      Vider le panier
                    </button>
                  </div>
                </div>
              </div>

              {/* Information sur les abonnements */}
              {hasSubscriptionItems && (
                <div className="alert alert-light border mb-4">
                  <div className="d-flex">
                    <div className="me-3 fs-4" style={{ color: "#543ba4" }}>
                      <i className="bi bi-info-circle"></i>
                    </div>
                    <div>
                      <h6 className="mb-2">Articles en option abonnement</h6>
                      <p className="small mb-1">
                        Vous avez choisi l'option abonnement pour certains
                        articles, ce qui vous permet d'économiser
                        <strong className="mx-1" style={{ color: "#f89638" }}>
                          {subscriptionSavings.toFixed(2)} €
                        </strong>
                        sur cette commande.
                      </p>
                      <p className="small mb-0">
                        L'abonnement est sans engagement et peut être modifié ou
                        résilié à tout moment depuis votre espace client.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="col-lg-4">
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-white py-3">
                  <h5 className="card-title mb-0">Récapitulatif</h5>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Sous-total</span>
                    <span>{totalPrice.toFixed(2)} €</span>
                  </div>

                  {hasSubscriptionItems && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Économies (abonnements)</span>
                      <span>-{subscriptionSavings.toFixed(2)} €</span>
                    </div>
                  )}

                  {discount > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Remise (code promo)</span>
                      <span>-{discountAmount.toFixed(2)} €</span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between mb-2">
                    <span>Livraison</span>
                    <span>
                      {finalTotal >= 50 ? (
                        "Gratuite"
                      ) : (
                        <>
                          <span className="text-muted text-decoration-line-through me-1">
                            4.99 €
                          </span>
                          <span className="text-success">Gratuite</span>
                        </>
                      )}
                    </span>
                  </div>

                  <hr />

                  <div className="d-flex justify-content-between mb-4">
                    <span className="fw-bold">Total</span>
                    <span className="fw-bold fs-5">
                      {finalTotal.toFixed(2)} €
                    </span>
                  </div>

                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Code promo"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      disabled={isProcessing}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={applyCoupon}
                      disabled={isProcessing}
                    >
                      Appliquer
                    </button>
                  </div>

                  <button
                    className="btn btn-primary w-100 py-2"
                    onClick={handleCheckout}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Traitement en cours...
                      </>
                    ) : (
                      <>Procéder au paiement</>
                    )}
                  </button>

                  <div className="mt-4">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-shield-check text-success me-2 fs-5"></i>
                      <div>
                        <h6 className="mb-0">Paiement sécurisé</h6>
                        <p className="mb-0 small text-muted">
                          Vos informations bancaires sont cryptées
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-truck text-success me-2 fs-5"></i>
                      <div>
                        <h6 className="mb-0">Livraison gratuite</h6>
                        <p className="mb-0 small text-muted">
                          À partir de 50€ d'achat
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-arrow-repeat text-success me-2 fs-5"></i>
                      <div>
                        <h6 className="mb-0">Retours gratuits</h6>
                        <p className="mb-0 small text-muted">
                          Pendant 30 jours
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommandations */}
              <div className="card border-0 bg-light mb-4">
                <div className="card-body p-3">
                  <h6 className="mb-3 text-center">
                    Vous pourriez aussi aimer
                  </h6>
                  <div className="row g-2">
                    <div className="col-6">
                      <Link to="/product/3" className="text-decoration-none">
                        <div className="card h-100 border-0 shadow-sm">
                          <img
                            src="https://via.placeholder.com/150"
                            className="card-img-top"
                            alt="Box Découverte"
                          />
                          <div className="card-body p-2">
                            <p className="card-title small mb-0 text-dark">
                              Box Découverte
                            </p>
                            <p className="text-primary small fw-bold mb-0">
                              39.99 €
                            </p>
                          </div>
                        </div>
                      </Link>
                    </div>
                    <div className="col-6">
                      <Link to="/product/4" className="text-decoration-none">
                        <div className="card h-100 border-0 shadow-sm">
                          <img
                            src="https://via.placeholder.com/150"
                            className="card-img-top"
                            alt="Box Édition Limitée"
                          />
                          <div className="card-body p-2">
                            <p className="card-title small mb-0 text-dark">
                              Box Édition Limitée
                            </p>
                            <p className="text-primary small fw-bold mb-0">
                              59.99 €
                            </p>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
